import { Field } from '@base-ui/react/field';
import { Fieldset } from '@base-ui/react/fieldset';
import { Form } from '@base-ui/react/form';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Autocomplete } from '@/components/ui/Autocomplete';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NumberField } from '@/components/ui/NumberField';
import { Spinner } from '@/components/ui/Spinner';
import { TextArea } from '@/components/ui/TextArea';
import { LOCATION_SUGGESTIONS } from '@/consts/locations';
import { useExtractProfile } from '@/hooks/useExtractProfile';
import { splitCsv } from '@/lib/utils';

interface ProfileStepProps {
  initialData: Record<string, unknown>;
  onBack: () => void;
  onNext: (profileData: Record<string, unknown>) => void;
  resumeText: string;
}

interface SkillsData {
  backend: string;
  cloudDevops: string;
  databases: string;
  frontend: string;
  languages: string;
  other: string;
}

interface PreferencesData {
  companySize: string;
  companyType: string;
  culture: string;
  stack: string;
}

interface FormState {
  experienceYears: number | null;
  locationPreference: string;
  minSalary: number | null;
  name: string;
  preferences: PreferencesData;
  requirements: string;
  skills: SkillsData;
  summary: string;
  targetRoles: string;
}

const emptySkills: SkillsData = {
  backend: '',
  cloudDevops: '',
  databases: '',
  frontend: '',
  languages: '',
  other: '',
};

const emptyPreferences: PreferencesData = {
  companySize: '',
  companyType: '',
  culture: '',
  stack: '',
};

const initialFormState: FormState = {
  experienceYears: null,
  locationPreference: '',
  minSalary: null,
  name: '',
  preferences: { ...emptyPreferences },
  requirements: '',
  skills: { ...emptySkills },
  summary: '',
  targetRoles: '',
};

const parseExtractedData = (data: Record<string, unknown>): FormState => {
  const skills = (data.skills ?? {}) as Record<string, unknown>;
  const prefs = (data.preferences ?? {}) as Record<string, unknown>;
  const targetRoles = Array.isArray(data.target_roles)
    ? (data.target_roles as string[]).join(', ')
    : String(data.target_roles ?? '');
  const requirements = Array.isArray(data.requirements)
    ? (data.requirements as string[]).join(', ')
    : String(data.requirements ?? '');

  return {
    experienceYears:
      data.experience_years != null ? Number(data.experience_years) : null,
    locationPreference: String(data.location_preference ?? ''),
    minSalary: data.min_salary != null ? Number(data.min_salary) : null,
    name: String(data.name ?? ''),
    preferences: {
      companySize: String(prefs.companySize ?? ''),
      companyType: String(prefs.companyType ?? ''),
      culture: String(prefs.culture ?? ''),
      stack: String(prefs.stack ?? ''),
    },
    requirements,
    skills: {
      backend: Array.isArray(skills.backend)
        ? skills.backend.join(', ')
        : String(skills.backend ?? ''),
      cloudDevops: Array.isArray(skills.cloudDevops)
        ? skills.cloudDevops.join(', ')
        : String(skills.cloudDevops ?? ''),
      databases: Array.isArray(skills.databases)
        ? skills.databases.join(', ')
        : String(skills.databases ?? ''),
      frontend: Array.isArray(skills.frontend)
        ? skills.frontend.join(', ')
        : String(skills.frontend ?? ''),
      languages: Array.isArray(skills.languages)
        ? skills.languages.join(', ')
        : String(skills.languages ?? ''),

      other: Array.isArray(skills.other)
        ? skills.other.join(', ')
        : String(skills.other ?? ''),
    },
    summary: String(data.summary ?? ''),
    targetRoles,
  };
};

export const ProfileStep = ({
  initialData,
  onBack,
  onNext,
  resumeText,
}: ProfileStepProps) => {
  const extract = useExtractProfile();
  const { mutate: triggerExtract } = extract;
  const [form, setForm] = useState<FormState>(
    Object.keys(initialData).length > 0
      ? parseExtractedData(initialData)
      : initialFormState,
  );

  useEffect(() => {
    if (resumeText && Object.keys(initialData).length === 0) {
      triggerExtract(resumeText, {
        onSuccess: (result) => {
          setForm(parseExtractedData(result.profile));
        },
      });
    }
  }, [resumeText, initialData, triggerExtract]);

  const errors: Record<string, string | string[]> = extract.error
    ? {
        root: `Extraction failed: ${extract.error.message}. You can fill in the form manually.`,
      }
    : {};

  const updateField = (field: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const updateSkill = (field: keyof SkillsData, value: string) =>
    setForm((prev) => ({
      ...prev,
      skills: { ...prev.skills, [field]: value },
    }));

  const updatePreference = (field: keyof PreferencesData, value: string) =>
    setForm((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, [field]: value },
    }));

  const handleSubmit = () => {
    const profileData: Record<string, unknown> = {
      experience_years: form.experienceYears,
      location_preference: form.locationPreference,
      min_salary: form.minSalary,
      name: form.name,
      preferences: JSON.stringify(form.preferences),
      requirements: JSON.stringify(splitCsv(form.requirements)),
      skills: JSON.stringify({
        backend: splitCsv(form.skills.backend),
        cloudDevops: splitCsv(form.skills.cloudDevops),
        databases: splitCsv(form.skills.databases),
        frontend: splitCsv(form.skills.frontend),
        languages: splitCsv(form.skills.languages),
        other: splitCsv(form.skills.other),
      }),
      summary: form.summary,
      target_roles: JSON.stringify(splitCsv(form.targetRoles)),
    };
    onNext(profileData);
  };

  if (extract.isPending) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <Spinner label="" size="lg" />
        <p className="text-sm lg:text-base text-foreground-muted">
          Extracting profile from resume...
        </p>
      </div>
    );
  }

  return (
    <Form
      className="space-y-5"
      errors={errors}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div>
        <h2 className="text-lg lg:text-xl font-semibold">
          Confirm what we extracted
        </h2>
        <p className="mt-1 text-sm lg:text-base text-foreground-muted">
          Edit anything off. This drives every match score.
        </p>
      </div>

      <Field.Root name="root">
        <Field.Error className="block text-center text-sm lg:text-base text-score-low" />
      </Field.Root>

      <div className="space-y-4">
        <Field.Root>
          <Field.Label className="text-sm lg:text-base font-medium text-foreground-muted">
            Name
          </Field.Label>
          <Input
            onChange={(event) => updateField('name', event.target.value)}
            placeholder="Your full name"
            value={form.name}
          />
        </Field.Root>

        <Field.Root>
          <Field.Label className="text-sm lg:text-base font-medium text-foreground-muted">
            Target Roles
          </Field.Label>
          <Input
            onChange={(event) => updateField('targetRoles', event.target.value)}
            placeholder="Senior Frontend Engineer, Staff Engineer, ..."
            value={form.targetRoles}
          />
        </Field.Root>

        <Fieldset.Root>
          <Fieldset.Legend className="text-sm lg:text-base font-medium text-foreground-muted mb-2">
            Skills
          </Fieldset.Legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(Object.keys(emptySkills) as (keyof SkillsData)[]).map(
              (category) => (
                <Field.Root key={category}>
                  <Field.Label className="text-xs lg:text-sm text-foreground-muted capitalize">
                    {category === 'cloudDevops' ? 'Cloud / DevOps' : category}
                  </Field.Label>
                  <Input
                    onChange={(event) =>
                      updateSkill(category, event.target.value)
                    }
                    placeholder={`${category} skills, comma-separated`}
                    value={form.skills[category]}
                  />
                </Field.Root>
              ),
            )}
          </div>
        </Fieldset.Root>

        <div className="grid grid-cols-2 gap-4">
          <Field.Root>
            <Field.Label className="text-sm lg:text-base font-medium text-foreground-muted">
              Experience (years)
            </Field.Label>
            <NumberField
              min={0}
              onValueChange={(v) =>
                setForm((prev) => ({ ...prev, experienceYears: v }))
              }
              step={1}
              value={form.experienceYears}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label className="text-sm lg:text-base font-medium text-foreground-muted">
              Minimum Salary ($)
            </Field.Label>
            <NumberField
              format={{
                currency: 'USD',
                maximumFractionDigits: 0,
                style: 'currency',
              }}
              min={0}
              onValueChange={(v) =>
                setForm((prev) => ({ ...prev, minSalary: v }))
              }
              step={5000}
              value={form.minSalary}
            />
          </Field.Root>
        </div>

        <Field.Root>
          <Field.Label className="text-sm lg:text-base font-medium text-foreground-muted">
            Location Preference
          </Field.Label>
          <Autocomplete
            items={[...LOCATION_SUGGESTIONS]}
            onValueChange={(value) => updateField('locationPreference', value)}
            placeholder="Remote US, San Francisco, ..."
            value={form.locationPreference}
          />
        </Field.Root>

        <Field.Root>
          <Field.Label className="text-sm lg:text-base font-medium text-foreground-muted">
            Requirements
          </Field.Label>
          <Input
            onChange={(event) =>
              updateField('requirements', event.target.value)
            }
            placeholder="Must-haves, comma-separated"
            value={form.requirements}
          />
        </Field.Root>

        <div>
          <p className="text-sm lg:text-base font-medium text-foreground-muted mb-2">
            Preferences
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(Object.keys(emptyPreferences) as (keyof PreferencesData)[]).map(
              (field) => (
                <Field.Root key={field}>
                  <Field.Label className="text-xs lg:text-sm text-foreground-muted capitalize">
                    {field === 'companySize'
                      ? 'Company Size'
                      : field === 'companyType'
                        ? 'Company Type'
                        : field}
                  </Field.Label>
                  <Input
                    onChange={(event) =>
                      updatePreference(field, event.target.value)
                    }
                    placeholder={field}
                    value={form.preferences[field]}
                  />
                </Field.Root>
              ),
            )}
          </div>
        </div>

        <Field.Root>
          <Field.Label className="text-sm lg:text-base font-medium text-foreground-muted">
            Summary
          </Field.Label>
          <TextArea
            className="min-h-25"
            onChange={(event) => updateField('summary', event.target.value)}
            placeholder="Brief professional summary..."
            resize
            value={form.summary}
          />
        </Field.Root>
      </div>

      <div className="flex justify-between">
        <Button onClick={onBack} size="md" type="button" variant="ghost">
          <ChevronLeft className="size-3.5" /> Back
        </Button>
        <Button size="md" type="submit" variant="primary">
          Continue <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </Form>
  );
};
